import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  EuiButton,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiRadioGroup,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import { UserContext } from "../Context";
import { getApiUrl } from "../utils/api";

const LabSectionModal = ({
  isOpen,
  onClose,
  onAssigned,
  forceSelection = false,
}) => {
  const { user, updateUser } = useContext(UserContext);
  const [labSections, setLabSections] = useState([]);
  const [defaultCondition, setDefaultCondition] = useState("A");
  const [selectedLab, setSelectedLab] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assigning, setAssigning] = useState(false);

  // We intentionally exclude user from deps so selection does not reset mid-session
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError("");

    fetch(getApiUrl("/api/experiment/labs"), {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    })
      .then(async (response) => {
        if (!isMounted) {
          return;
        }
        if (!response.ok) {
          throw new Error(`Failed to load lab sections (status ${response.status})`);
        }
        const data = await response.json();
        const sections = Array.isArray(data.labSections) ? data.labSections : [];
        setLabSections(sections);
        setDefaultCondition(data.defaultCondition || "A");

        const currentLab = user?.lab_section;
        if (currentLab) {
          setSelectedLab(currentLab);
        } else if (sections.length === 1) {
          setSelectedLab(sections[0].id);
        } else {
          setSelectedLab("");
        }
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        setError(err.message || "Failed to load lab sections");
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (user?.lab_section && isOpen) {
      setSelectedLab(user.lab_section);
    }
  }, [user?.lab_section, isOpen]);

  const radioOptions = useMemo(
    () =>
      labSections.map((section) => ({
        id: section.id,
        label: section.label || section.id,
        "data-test-subj": `lab-section-${section.id}`,
      })),
    [labSections]
  );

  const handleAssign = async () => {
    if (!selectedLab) {
      setError("Please select your lab section.");
      return;
    }

    setAssigning(true);
    setError("");

    try {
      const response = await fetch(getApiUrl("/api/experiment/assign"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lab_section: selectedLab }),
      });

      const data = await response.json();
      if (!response.ok || data.status !== "success") {
        throw new Error(data.message || data.error || "Assignment failed");
      }

      const updatedUser = {
        ...(user || {}),
        lab_section: data.lab_section,
        experiment_condition: data.experiment_condition,
        lab_assigned_at: new Date().toISOString(),
      };
      updateUser(updatedUser);
      if (onAssigned) {
        onAssigned(updatedUser);
      }
      if (!forceSelection && onClose) {
        onClose();
      }
    } catch (err) {
      setError(err.message || "Failed to assign lab section");
    } finally {
      setAssigning(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <EuiModal onClose={forceSelection ? () => {} : onClose}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          Select Your Lab Section
        </EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiText>
          <p>
            Please choose the lab section you attend so we can configure your workspace correctly.
          </p>
        </EuiText>
        <EuiSpacer size="m" />

        {loading ? (
          <EuiFlexGroup justifyContent="center">
            <EuiFlexItem grow={false}>
              <EuiLoadingSpinner size="l" />
            </EuiFlexItem>
          </EuiFlexGroup>
        ) : (
          <>
            {error && (
              <>
                <EuiCallOut title="Something went wrong" color="danger" iconType="alert">
                  <p>{error}</p>
                </EuiCallOut>
                <EuiSpacer size="m" />
              </>
            )}

            {labSections.length === 0 ? (
              <EuiCallOut title="No lab sections configured" color="warning">
                <p>
                  We could not find any lab sections in the system. Please contact your instructor or try again later. Default condition {defaultCondition} will be used.
                </p>
              </EuiCallOut>
            ) : (
              <EuiRadioGroup
                options={radioOptions}
                idSelected={selectedLab}
                onChange={setSelectedLab}
                disabled={assigning}
              />
            )}
          </>
        )}
      </EuiModalBody>
      <EuiModalFooter>
        {!forceSelection && (
          <EuiButton color="text" onClick={onClose} disabled={assigning}>
            Cancel
          </EuiButton>
        )}
        <EuiButton
          onClick={handleAssign}
          fill
          isLoading={assigning}
          disabled={loading || labSections.length === 0}
        >
          Save Lab Section
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};

export default LabSectionModal;
